/**
 * Turns HTTP traffic into the two engineering questions GA can actually answer:
 * how often requests fail, and how often they are slow enough that a user
 * notices.
 *
 * Only transport faults and server faults count as failures here. On a sign-in
 * service most 4xx answers are the service working as intended — a wrong
 * password, an expired code, a rate limit — and those are already reported by
 * the screens as typed outcomes. Counting them again as errors would bury the
 * real faults under every mistyped password.
 *
 * Nothing here can change a request, a response, or an error.
 */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { sanitizeAnalyticsPath, trackEvent } from '../analytics';

/** Above this, a request is slow enough that someone waited for it. */
const SLOW_REQUEST_MS = 2_000;

/**
 * Ceiling per page load, per kind. A backend in trouble can make every call
 * fail at once; without a cap that becomes a burst of events that costs GA
 * quota and tells you nothing the first twenty did not.
 */
const MAX_EVENTS_PER_LOAD = 20;

/** Static assets say nothing about API health. */
const IGNORED_PATH = /\/assets\/|\.(json|png|jpe?g|svg|woff2?|css|js)(\?|$)/i;

/** No response at all, or the server's own fault. */
function isFault(status: number): boolean {
  return status === 0 || status >= 500;
}

@Injectable()
export class AnalyticsInterceptor implements HttpInterceptor {
  private slowReported = 0;
  private errorsReported = 0;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (IGNORED_PATH.test(request.url)) return next.handle(request);

    const startedAt = Date.now();

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (!(event instanceof HttpResponse)) return;

          const duration = Date.now() - startedAt;
          if (duration < SLOW_REQUEST_MS) return;
          if (this.slowReported >= MAX_EVENTS_PER_LOAD) return;
          this.slowReported++;

          trackEvent('api_timing', {
            endpoint: this.endpointOf(request),
            duration_ms: duration,
            status_code: event.status,
          });
        },
        error: (error: unknown) => {
          if (!(error instanceof HttpErrorResponse)) return;
          if (!isFault(error.status)) return;
          if (this.errorsReported >= MAX_EVENTS_PER_LOAD) return;
          this.errorsReported++;

          // The status and the route, never the response body — an error body
          // can quote the address that was typed.
          trackEvent('app_error', {
            error_description: `http_${error.status || 'network'}`,
            error_code: error.status,
            endpoint: this.endpointOf(request),
          });
        },
      }),
    );
  }

  /**
   * The request path with ids masked and the query string dropped, so
   * `/sso/login-challenge?username=jane@lab.com` is reported as
   * `GET /sso/login-challenge`.
   */
  private endpointOf(request: HttpRequest<unknown>): string {
    let path = request.url;
    try {
      path = new URL(request.url, window.location.origin).pathname;
    } catch {
      // Relative or malformed URL; sanitizing the raw value is still safe.
    }
    return `${request.method} ${sanitizeAnalyticsPath(path)}`;
  }
}
